/**
 * A draft package: an `ActivityManifest` being written. Server-backed where the catalog offers
 * `/v1/activities/drafts`, and falling back to this browser's own storage where it does not.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ActivityManifest } from "@nebulize/sdk";

import { ApiError, api } from "@/lib/api";

const STORE_KEY = "nebulize.studio.drafts.v1";

/** What a draft is being written as. A bundle carries members; an activity carries behaviour. */
export type DraftKind = "activity" | "bundle";

/** Where this draft's record actually lives right now. `local` earns the "local only" badge. */
export type DraftOrigin = "server" | "local";

/** A scene document keyed by canvas kind, in the shape `scenes_json` carries. The scene editor's seam. */
export type DraftScenes = Readonly<Record<string, unknown>>;

export interface Draft {
  readonly draft_id: string;
  /** The stable, human-shaped address the studio's own url is built from. */
  readonly slug: string;
  readonly kind: DraftKind;
  readonly created_at: string;
  readonly updated_at: string;
  readonly manifest: ActivityManifest;
  readonly scenes: DraftScenes;
  readonly origin: DraftOrigin;
}

export interface DraftList {
  readonly drafts: Draft[];
  readonly local: boolean;
}

/** An empty manifest of each kind, with every collection present. */
export function blankManifest(kind: DraftKind, id: string): ActivityManifest {
  const base: ActivityManifest = {
    schema_version: 1,
    activity: { id, name: "", version: "0.1.0", sdk: "^0.1.0", description: "" },
    entities: {},
    effects: {},
    abilities: {},
    canvases: {},
    submissions: {},
    settings: [],
    requires: {},
    provides: {},
    visibility: {},
    regions: {},
    events: { publishes: [], exports: {}, triggers: {} },
  };
  if (kind === "bundle") {
    return { ...base, bundle: { members: [], proposed_bindings: [], removes_members: false } };
  }
  return base;
}

function kindOf(manifest: ActivityManifest): DraftKind {
  return manifest.bundle != null ? "bundle" : "activity";
}

function now(): string {
  return new Date().toISOString();
}

function mint(): string {
  return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function byUpdated(left: Draft, right: Draft): number {
  return right.updated_at.localeCompare(left.updated_at);
}

// --- The local fallback engine. Used only where the server route is not there to ask. ---

/** A row written before `slug` existed gets its own id as its address, on read. */
function withSlug(row: Draft): Draft {
  return row.slug === undefined || row.slug === "" ? { ...row, slug: row.draft_id } : row;
}

function readAllLocal(): Draft[] {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw === null) return [];
    const held: unknown = JSON.parse(raw);
    return Array.isArray(held) ? (held as Draft[]).map(withSlug) : [];
  } catch {
    return [];
  }
}

function writeAllLocal(drafts: readonly Draft[]): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(drafts));
  } catch {
    return;
  }
}

function listLocal(): Draft[] {
  return [...readAllLocal()].sort(byUpdated);
}

function readLocal(draftId: string): Draft | undefined {
  return readAllLocal().find((one) => one.draft_id === draftId);
}

function createLocal(manifest: ActivityManifest): Draft {
  const draftId = mint();
  const made: Draft = {
    draft_id: draftId,
    slug: draftId,
    kind: kindOf(manifest),
    created_at: now(),
    updated_at: now(),
    manifest,
    scenes: {},
    origin: "local",
  };
  writeAllLocal([made, ...readAllLocal()]);
  return made;
}

function saveLocal(draftId: string, manifest: ActivityManifest): Draft {
  const held = readAllLocal();
  const found = held.find((one) => one.draft_id === draftId);
  const next: Draft =
    found === undefined
      ? {
          draft_id: draftId,
          slug: draftId,
          kind: kindOf(manifest),
          created_at: now(),
          updated_at: now(),
          manifest,
          scenes: {},
          origin: "local",
        }
      : { ...found, manifest, updated_at: now() };
  writeAllLocal([next, ...held.filter((one) => one.draft_id !== draftId)]);
  return next;
}

function removeLocal(draftId: string): void {
  writeAllLocal(readAllLocal().filter((one) => one.draft_id !== draftId));
}

// --- The server-backed reads and writes, each degrading to the engine above on a 404 route. ---

/** The wire shape `/v1/activities/drafts` actually answers: `manifest_json` and `scenes_json`.
 * `slug` is optional here only so an older mock or fixture missing the field still parses; a real
 * answer from the api always carries one. */
interface ApiDraft {
  readonly draft_id: string;
  readonly slug?: string;
  readonly kind: DraftKind;
  readonly manifest_json: Record<string, unknown>;
  readonly scenes_json: Record<string, unknown>;
  readonly created_at: string;
  readonly updated_at: string;
}

/** Patches in `ActivityManifest`'s only two non-optional fields when `manifest_json` lacks them. */
function normalizeManifest(raw: Record<string, unknown>): ActivityManifest {
  const partial = raw as Partial<ActivityManifest>;
  return {
    ...partial,
    schema_version: partial.schema_version ?? 1,
    activity: partial.activity ?? { id: "", name: "", version: "", sdk: "" },
  } as ActivityManifest;
}

function fromApi(row: ApiDraft): Draft {
  return {
    draft_id: row.draft_id,
    slug: row.slug ?? row.draft_id,
    kind: row.kind,
    created_at: row.created_at,
    updated_at: row.updated_at,
    manifest: normalizeManifest(row.manifest_json),
    scenes: row.scenes_json,
    origin: "server",
  };
}

/** A 404 with no structured code is Starlette's own "no such route", not the app's "no such draft". */
function isEndpointMissing(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.code === "unknown";
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether an address in the url is a bookmarked row id rather than a slug. A slug is
 * `[a-z0-9-]+` and never takes this shape. */
export function isUuidShaped(value: string): boolean {
  return UUID_SHAPE.test(value);
}

export const draftKeys = {
  all: ["studio", "drafts"] as const,
  list: () => [...draftKeys.all, "list"] as const,
  detail: (draftId: string) => [...draftKeys.all, draftId] as const,
  address: (address: string) => [...draftKeys.all, "address", address] as const,
};

export function useDraftList(): UseQueryResult<DraftList> {
  return useQuery({
    queryKey: draftKeys.list(),
    queryFn: async (): Promise<DraftList> => {
      try {
        const rows = await api.get<ApiDraft[]>("/v1/activities/drafts");
        return { drafts: rows.map(fromApi).sort(byUpdated), local: false };
      } catch (error) {
        if (isEndpointMissing(error)) return { drafts: listLocal(), local: true };
        throw error;
      }
    },
    staleTime: 30 * 1000,
  });
}

export function useDraft(draftId: string): UseQueryResult<Draft | undefined> {
  return useQuery({
    queryKey: draftKeys.detail(draftId),
    queryFn: async (): Promise<Draft | undefined> => {
      try {
        return fromApi(await api.get<ApiDraft>(`/v1/activities/drafts/${draftId}`));
      } catch (error) {
        if (isEndpointMissing(error)) return readLocal(draftId);
        if (error instanceof ApiError && error.status === 404) return undefined;
        throw error;
      }
    },
  });
}

/** The slug for a draft id, or undefined if it cannot be resolved. */
export async function slugForDraftId(draftId: string): Promise<string | undefined> {
  try {
    return (await api.get<ApiDraft>(`/v1/activities/drafts/${draftId}`)).slug ?? draftId;
  } catch {
    return undefined;
  }
}

/** A draft by its studio-url address: a slug, a bookmarked uuid, or a local-only draft's own id. */
export function useDraftByAddress(address: string): UseQueryResult<Draft | undefined> {
  return useQuery({
    queryKey: draftKeys.address(address),
    queryFn: async (): Promise<Draft | undefined> => {
      if (isUuidShaped(address)) {
        try {
          return fromApi(await api.get<ApiDraft>(`/v1/activities/drafts/${address}`));
        } catch (error) {
          if (isEndpointMissing(error)) return readLocal(address);
          if (error instanceof ApiError && error.status === 404) return undefined;
          throw error;
        }
      }
      try {
        return fromApi(await api.get<ApiDraft>(`/v1/activities/drafts/by-slug/${address}`));
      } catch (error) {
        if (isEndpointMissing(error)) return readLocal(address);
        if (error instanceof ApiError && error.status === 404) return readLocal(address);
        throw error;
      }
    },
  });
}

/** Start a draft from a manifest: blank for a new one, or a fork or next version of a published one. */
export function useCreateDraft(): UseMutationResult<Draft, Error, ActivityManifest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (manifest: ActivityManifest): Promise<Draft> => {
      try {
        return fromApi(
          await api.post<ApiDraft>("/v1/activities/drafts", {
            kind: kindOf(manifest),
            manifest_json: manifest,
            scenes_json: {},
          }),
        );
      } catch (error) {
        if (!isEndpointMissing(error)) throw error;
        return createLocal(manifest);
      }
    },
    onSuccess: (saved) => {
      client.setQueryData(draftKeys.detail(saved.draft_id), saved);
      void client.invalidateQueries({ queryKey: draftKeys.list() });
    },
  });
}

export interface SaveDraftInput {
  readonly draft: Draft;
  readonly manifest: ActivityManifest;
  /** Defaults to `draft.scenes`: the route replaces both fields together on every save. */
  readonly scenes?: DraftScenes;
}

function saveLocalWith(draftId: string, manifest: ActivityManifest, scenes: DraftScenes): Draft {
  const saved = saveLocal(draftId, manifest);
  const held = readAllLocal();
  const withScenes = { ...saved, scenes };
  writeAllLocal([withScenes, ...held.filter((one) => one.draft_id !== draftId)]);
  return withScenes;
}

export function useSaveDraft(): UseMutationResult<Draft, Error, SaveDraftInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ draft, manifest, scenes }: SaveDraftInput): Promise<Draft> => {
      const carried = scenes ?? draft.scenes;
      if (draft.origin === "local") return saveLocalWith(draft.draft_id, manifest, carried);
      try {
        return fromApi(
          await api.put<ApiDraft>(`/v1/activities/drafts/${draft.draft_id}`, {
            manifest_json: manifest,
            scenes_json: carried,
          }),
        );
      } catch (error) {
        if (!isEndpointMissing(error)) throw error;
        return saveLocalWith(draft.draft_id, manifest, carried);
      }
    },
    onSuccess: (saved) => {
      client.setQueryData(draftKeys.detail(saved.draft_id), saved);
      void client.invalidateQueries({ queryKey: draftKeys.list() });
    },
  });
}

export function useDeleteDraft(): UseMutationResult<void, Error, Draft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (draft: Draft): Promise<void> => {
      if (draft.origin === "local") {
        removeLocal(draft.draft_id);
        return;
      }
      try {
        await api.delete<undefined>(`/v1/activities/drafts/${draft.draft_id}`);
      } catch (error) {
        if (!isEndpointMissing(error)) throw error;
        removeLocal(draft.draft_id);
      }
    },
    onSuccess: (_deleted, draft) => {
      client.removeQueries({ queryKey: draftKeys.detail(draft.draft_id) });
      void client.invalidateQueries({ queryKey: draftKeys.list() });
    },
  });
}
